<template>
  <div id="email-preferences" class="fill-height align-start">
    <i-overlay
      v-if="loading && $vuetifyDisplay.smAndDown"
      text="Loading email preferences..."
    />

    <v-row v-else class="g-container" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <v-row>
          <v-spacer></v-spacer>
          <v-col cols="12" md="3" lg="2">
            <i-instructions
              instructionKey="account/email-preferences"
            ></i-instructions>
          </v-col>
        </v-row>

        <i-progress-linear v-if="loading && !$vuetifyDisplay.smAndDown" />

        <template v-else>
          <v-row>
            <v-col cols="12">
              <v-card>
                <div class="p-div-emails">
                  <v-form
                    v-model="form"
                    :disabled="!editEmailPreferences"
                    @submit.prevent
                  >
                    <div
                      class="i-email-preferences-div-email"
                      v-for="email in mutableEmailPreferences"
                      :key="email.emailType"
                    >
                      <div class="i-email-preferences-div-email-inner">
                        <v-checkbox
                          hide-details
                          class="ma-0"
                          v-model="email.receive"
                        >
                          <template v-slot:label>
                            <div class="i-email-preferences-div-email-text">
                              <p
                                class="g-bold g-heading-xs"
                                :class="{
                                  'g-text-color-black': editEmailPreferences
                                }"
                              >
                                {{ email.emailTitle }}
                              </p>
                              <p>{{ email.emailDescription }}</p>
                            </div>
                          </template>
                        </v-checkbox>
                      </div>
                      <v-divider class="pa-0 ma-0"></v-divider>
                    </div>
                  </v-form>
                </div>

                <div class="d-flex p-div-actions justify-center justify-md-end">
                  <template v-if="!editEmailPreferences">
                    <v-btn
                      rounded
                      class="i-btn-block-secondary"
                      @click="editEmailPreferences = true"
                    >
                      Edit
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn
                      rounded
                      text
                      class="i-btn-text-black"
                      @click="resetEmailPreferences()"
                    >
                      Cancel
                    </v-btn>
                    <v-btn
                      rounded
                      class="i-btn-block-secondary"
                      :loading="updatingEmailPreferences"
                      :disabled="!changesMade"
                      @click="saveEmailPreferences()"
                    >
                      <span>Save</span>
                    </v-btn>
                  </template>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import {
  fetchEmailPreferences,
  updateEmailPreferences
} from '@/services/emailPreferences'
import iOverlay from '@/components/Overlay'
import iInstructions from '@/components/Instructions'
import iProgressLinear from '@/components/ProgressLinear'

export default {
  name: 'email-preferences',

  components: {
    iInstructions,
    iOverlay,
    iProgressLinear
  },

  data: () => ({
    form: undefined,
    emailPreferences: [],
    editEmailPreferences: false,
    loading: true,
    mutableEmailPreferences: [],
    updatingEmailPreferences: false
  }),

  computed: {
    ...mapGetters('tenant', [
      'featureDeliveryConfirmationEnabled',
      'orderChatEnabled'
    ]),

    changesMade() {
      return !_.isEqual(this.mutableEmailPreferences, this.emailPreferences)
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    fetchEmailPreferences() {
      this.loading = true
      fetchEmailPreferences()
        .then((response) => {
          this.emailPreferences = response.filter(
            (p) =>
              p.emailType !== 'ORDER_NOT_RECEIVED' ||
              this.featureDeliveryConfirmationEnabled
          )
          this.emailPreferences = this.emailPreferences.filter(
            (p) =>
              p.emailType !== 'ORDER_CHAT_UNREAD_REMINDER' ||
              this.orderChatEnabled
          )
          this.mutableEmailPreferences = _.cloneDeep(this.emailPreferences)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Email preferences could not be loaded, please try again...'
          })
        })
        .finally(() => {
          this.loading = false
        })
    },

    resetEmailPreferences() {
      this.editEmailPreferences = false
      this.mutableEmailPreferences = _.cloneDeep(this.emailPreferences)
    },

    saveEmailPreferences() {
      this.updatingEmailPreferences = true

      const payload = {
        preferences: this.mutableEmailPreferences.map((email) => {
          return {
            emailType: email.emailType,
            receive: email.receive
          }
        })
      }

      updateEmailPreferences(payload)
        .then(() => {
          this.addNotification({
            color: 'secondary',
            text: 'Email preferences updated!'
          })
          this.emailPreferences = _.cloneDeep(this.mutableEmailPreferences)
        })
        .catch(() => {
          this.addNotification({
            color: 'error',
            text: 'Email preferences could not be updated, please try again...'
          })
        })
        .finally(() => {
          this.updatingEmailPreferences = this.editEmailPreferences = false
        })
    }
  },

  mounted() {
    this.fetchEmailPreferences()
  }
}
</script>

<style lang="scss" scoped>
#email-preferences {
  .p-div-actions {
    padding: 12px 8px 12px 8px;
    position: sticky;
    bottom: 0;
    border-top: 1px solid var(--light-grey-color);
    margin-left: 0;
    width: 100%;
    background-color: #fff;
  }
  .p-div-emails {
    padding-bottom: 16px;
  }
  .p-div-email {
    &-inner {
      padding: 16px 14px 0px 20px;
      margin: 0px;
      &:hover {
        background-color: $bg-primary-color;
        transition: 0.4s background-color;
      }
    }
    &-text {
      margin-left: 12px;
    }
  }
}

@media only screen and (min-width: 600px) {
  #email-preferences {
    .p-div-actions {
      padding: 12px 16px 12px 16px;
    }
    .p-div-email {
      &-inner {
        padding: 18px 16px 4px 32px;
      }
      &-text {
        margin-left: 16px;
      }
    }
  }
}
</style>
